/** @format */

const { addressFlow } = require("../config/screens/address");
const { getTransitionScreens } = require("../config/screens/transition");
const { getPersonScreens } = require("../config/screens/person");
const { getJobScreens } = require("../config/screens/job");
const { getVerificationScreens } = require("../config/screens/verification");

function summaryScreenSettings(
  store,
  i18n,
  { addressFormatter, getLocalCountryName }
) {
  const { state } = store;

  const itemsToDisplay = [
    {
      id: "fullName",
      editId: "full-name",
      label: i18n.t("fullName:full_name"),
      value: state.user.fullName,
    },
    {
      id: "address",
      label: i18n.t("flowConfirmation:address"),
      value: addressFormatter(state.user),
    },
    {
      id: "birthdate",
      label: i18n.t("flowConfirmation:birthdate"),
      value: ({ state }) => {
        const { app } = state;
        const birthdate = new Date(state.user.birthdate);
        const birthday = birthdate.getUTCDate().toString();
        const birthmonth = (birthdate.getUTCMonth() + 1).toString();

        return (app.country.dobFormat || "DD.MM.YY")
          .replace("DD", birthday.padStart(2, "0"))
          .replace("MM", birthmonth.padStart(2, "0"))
          .replace("YY", birthdate.getUTCFullYear().toString().slice(2, 4));
      },
      isEditable: true,
    },
    {
      id: "industry",
      editId: "generic-select",
      label: i18n.t("job:otherIndustry.title"),
      value: ({ state }) => {
        const { industry } = state.job;
        return i18n.t(`job:industry.industries.${industry.id}`);
      },
      editParams: {
        placeholder: () => i18n.t("job:industry.search"),
        header: ({ state }) =>
          state.job.industry ? i18n.t("base:edit") : i18n.t("base:add"),
        options: (store) => {
          const { industries = [] } = store.state.partner || {};
          return industries.map((value) => ({
            value,
            label: i18n.t(`job:industry.industries.${value}`),
          }));
        },
        sortOptions: false,
        onSubmit: (selectedOption, { store }) => {
          store.dispatch("updateJob", {
            industry: {
              id: selectedOption.value,
              name: selectedOption.label,
            },
          });
        },
      },
    },
    {
      id: "occupation",
      editId: "generic-select",
      label: i18n.t("job:occupation.title"),
      value: ({ state }) => {
        const { occupation } = state.job;
        return i18n.t(`job:occupation.occupations.${occupation.id}`);
      },
      editParams: {
        placeholder: () => i18n.t("job:occupation.search"),
        header: ({ state }) =>
          state.job.occupation ? i18n.t("base:edit") : i18n.t("base:add"),
        options: (store) => {
          const { occupations = [] } = store.state.partner || {};
          return occupations.map((value) => ({
            value,
            label: i18n.t(`job:occupation.occupations.${value}`),
          }));
        },
        sortOptions: false,
        onSubmit: (selectedOption, { store }) => {
          console.log(selectedOption);
          store.dispatch("updateJob", {
            occupation: {
              id: selectedOption.value,
              name: selectedOption.label,
            },
          });
        },
      },
    },
  ];

  const optionalItems = [
    {
      id: "citizenship",
      editId: "citizenship",
      label: i18n.t("legal:optional_fields.citizenship"),
      value: getLocalCountryName(state.employer?.citizenship, i18n),
      isOptional: true,
      editParams: {
        header: ({ state }) =>
          state.employer?.citizenship
            ? i18n.t("base:edit")
            : i18n.t("base:add"),
        onSubmit: ({ citizenship }, { store }) =>
          store.dispatch("updateEmployer", { citizenship }),
      },
    },
    {
      id: "employer-name",
      editId: "generic-text-edit",
      label: i18n.t("legal:optional_fields.employer_name"),
      value: state.employer?.name,
      isOptional: true,
      editParams: {
        header: ({ state }) =>
          state.employer?.name ? i18n.t("base:edit") : i18n.t("base:add"),
        fields: [
          {
            name: "employerName",
            value: (store) => store.state.employer?.name,
            title: () => i18n.t("legal:optional_fields.employer_name"),
            placeholder: () =>
              i18n.t("legal:optional_fields.employer_name_placeholder"),
          },
        ],
        onSubmit: ({ employerName }, { store }) =>
          store.dispatch("updateEmployer", { name: employerName }),
      },
    },
    {
      id: "employer-address",
      editId: "address",
      label: i18n.t("legal:optional_fields.employer_address"),
      value: addressFormatter(state.employer?.address),
      isOptional: true,
      editParams: {
        dispatchKey: "updateEmployer",
        transformFieldPropertyName: (propertyName) => `address.${propertyName}`,
        getFieldValue: (fieldName) =>
          state.employer?.address?.[fieldName] || "",
        isValid: (params) => {
          return !!params.value;
        },
      },
    },
    {
      id: "employer-phone-number",
      editId: "phone-number",
      label: i18n.t("legal:optional_fields.employer_phone_number"),
      value: state.employer?.phone,
      isOptional: true,
      editParams: {
        dispatchKey: "updateEmployer",
        placeholder:
          i18n.t("phone:phone_number") || i18n.t("phone:mobile_number"),
      },
    },
  ];

  return [...itemsToDisplay, ...optionalItems];
}

function canadianFlow(i18n) {
  return [
    {
      path: "/chapter1",
      params: {
        title: () => i18n.t("overview:step_one"),
      },
      screens: [
        {
          type: "email",
          params: {
            collectBlackbox: true,
          },
        },
      ],
    },
    {
      path: "/chapter2",
      params: {
        title: () => i18n.t("overview:step_two"),
      },
      screens: [
        ...getPersonScreens({ i18n, screens: [{ name: "personalPassword" }] }),

        { type: "phone-number" },
        { type: "email-validation", params: { validation: true } },
      ],
    },
    {
      path: "/chapter3",
      params: {
        title: () => i18n.t("overview:step_three"),
      },
      screens: [
        ...getPersonScreens({
          i18n,
          screens: [{ name: "fullName" }],
        }),
        ...addressFlow(i18n),
        {
          type: "masked-birthdate",
          params: {
            // send Iovation request
            checkBlackBoxInIovation: true,
          },
        },

        ...getTransitionScreens({ i18n, screens: [{ name: "transitionJob" }] }),
        ...getJobScreens({
          i18n,
          screens: [{ name: "industry" }, { name: "occupation" }],
        }),
        ...[{ type: "phone-number-validation" }],

        {
          type: "summary",
          params: {
            items: summaryScreenSettings,
            onConfirm: async (store, { process }) => {
              await store.dispatch("updateSanction", {
                firstName: store.state.user.firstName,
                lastName: store.state.user.lastName,
              });
              process();
            },
          },
        },

        {
          type: "GDPR",
          params: {
            hint: () => i18n.t("GDPR:footer", { age: 19 }),
          },
        },

        ...getVerificationScreens({ i18n, screens: [{ name: "smsCode" }] }),
      ],
    },
  ];
}

module.exports = canadianFlow;



// WEBPACK FOOTER //
// ./src/partners/variant-flows/canada.js